import { useMemo } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import Lozenge from '@atlaskit/lozenge';
import { Flex, Text } from '@atlaskit/primitives';

import { toBBUser } from 'src/components/conversation-provider/utils';
import { User } from 'src/components/pipelines/models/User';
import { formatDistanceToNow } from 'src/components/pipelines/utils';
import { UserAvatar } from 'src/containers/user-avatar';

import { messages } from '../messages';
import { TestCaseActions } from '../types';

type TestUpdatedByProps = {
  lastActionAt: string;
  lastActionByUser: User;
  lastActionTaken: TestCaseActions;
};

export const TestUpdatedBy = ({
  lastActionAt,
  lastActionByUser,
  lastActionTaken,
}: TestUpdatedByProps) => {
  const { formatMessage } = useIntl();

  const bbUser = useMemo(
    () =>
      toBBUser({
        id: lastActionByUser.uuid,
        name: lastActionByUser.displayName,
        type: lastActionByUser.class_type,
        avatarUrl: lastActionByUser.avatarUrl,
        profileUrl: lastActionByUser.accountUrl,
      }),
    [lastActionByUser]
  );

  const messageContent = useMemo(() => {
    const user = bbUser.display_name;
    switch (lastActionTaken) {
      case TestCaseActions.MARKED_FLAKY:
        return {
          descriptor: messages.markedAsActionByUser,
          values: {
            action: (
              <Lozenge appearance="moved">
                {formatMessage(messages.flaky)}
              </Lozenge>
            ),
            user,
          },
        };
      case TestCaseActions.MARKED_OK:
        return {
          descriptor: messages.markedAsActionByUser,
          values: {
            action: (
              <Lozenge appearance="success">
                {formatMessage(messages.ok)}
              </Lozenge>
            ),
            user,
          },
        };
      case TestCaseActions.MARKED_QUARANTINE:
        return {
          descriptor: messages.markedAsActionByUser,
          values: {
            action: (
              <Lozenge appearance="default">
                {formatMessage(messages.quarantined)}
              </Lozenge>
            ),
            user,
          },
        };
      case TestCaseActions.UNMARKED_QUARANTINE:
        return {
          descriptor: messages.unmarkedAsActionByUser,
          values: {
            action: (
              <Lozenge appearance="inprogress">
                {formatMessage(messages.quarantined)}
              </Lozenge>
            ),
            user,
          },
        };
      default:
        return null;
    }
  }, [lastActionTaken, bbUser.display_name, formatMessage]);

  return (
    <Flex gap="space.100" testId="test-updated-by-user">
      <UserAvatar user={bbUser} />
      <Flex direction="column">
        {messageContent && (
          <>
            <Text>
              <FormattedMessage
                {...messageContent.descriptor}
                values={messageContent.values}
              />
            </Text>
            <Text size="small" color="color.text.subtlest">
              {formatDistanceToNow(new Date(lastActionAt))}
            </Text>
          </>
        )}
      </Flex>
    </Flex>
  );
};
